import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { Search, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

/** Búsqueda inversa: nombre + apellido -> posibles DNI (padrón AFIP). Al elegir uno completa DNI, nombre y apellido. */
export function NameDniSearch({ name, surname, onPick, testIdPrefix = "dni" }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [msg, setMsg] = useState("");

  const search = async () => {
    const q = `${surname || ""} ${name || ""}`.trim();
    if (q.length < 4) { setResults(null); setMsg("Escribí nombre y apellido para buscar el DNI"); return; }
    setLoading(true); setMsg(""); setResults(null);
    try {
      const r = await api.get("/lookup/name", { params: { q } });
      const list = r.data?.results || [];
      setResults(list);
      if (!list.length) setMsg(r.data?.reason === "error_externo"
        ? "No se pudo consultar el padrón en este momento. Completá el DNI a mano."
        : "No se encontraron personas con ese nombre en el padrón.");
    } catch {
      setMsg("No se pudo consultar, completá el DNI a mano");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5, margin: "2px 0 6px" }}>
      <button type="button" onClick={search} disabled={loading} data-testid={`${testIdPrefix}-name-search-btn`}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, alignSelf: "flex-start", padding: "8px 14px",
          border: "1.5px solid #0A0A0A", background: "#fff", color: "#0A0A0A", fontWeight: 800, fontSize: 11,
          borderRadius: 6, cursor: loading ? "wait" : "pointer", textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {loading ? <Loader2 size={13} className="dni-spin" /> : <Search size={13} />}
        {loading ? "Buscando..." : "Buscar DNI por nombre"}
      </button>
      {msg && <span data-testid={`${testIdPrefix}-name-search-status`} style={{ fontSize: 11, fontWeight: 700, color: "var(--invict-text-muted)", display: "inline-flex", alignItems: "center", gap: 5 }}><AlertCircle size={12} /> {msg}</span>}
      {results && results.length > 0 && (
        <div data-testid={`${testIdPrefix}-name-search-results`} style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 420 }}>
          <span style={{ fontSize: 10, color: "var(--invict-text-muted)", fontWeight: 700 }}>Elegí la persona (datos del padrón, revisá y confirmá):</span>
          {results.map((p, i) => (
            <button type="button" key={`${p.dni}-${i}`} data-testid={`${testIdPrefix}-name-result-${i}`}
              onClick={() => { onPick(p); setResults(null); setMsg(""); }}
              style={{ textAlign: "left", padding: "6px 10px", border: "1px solid #ddd", borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: 12 }}>
              <b>{p.denominacion}</b> · DNI {p.dni}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function DniLookup({ dni, onResolved, testIdPrefix = "dni" }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  // Siempre refleja el DNI actual del input (aunque cambie mientras hay un fetch en curso)
  const currentDniRef = useRef(dni);
  currentDniRef.current = dni;

  // Si el DNI cambia (el operador edita el input), se descarta cualquier resultado/estado anterior
  useEffect(() => {
    setStatus(null);
  }, [dni]);

  const lookup = async () => {
    const clean = (dni || "").replace(/\D/g, "");
    if (clean.length < 7 || clean.length > 8) {
      setStatus({ ok: false, msg: "Ingresá un DNI válido (7-8 dígitos)" });
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const r = await api.get(`/lookup/dni/${clean}`);
      // Si el DNI cambió mientras esperábamos la respuesta, la ignoramos (evita datos cruzados)
      const isStale = (currentDniRef.current || "").replace(/\D/g, "") !== clean;
      if (isStale) return;
      if (r.data?.found) {
        onResolved({ name: r.data.name || "", surname: r.data.surname || "" });
        setStatus({
          ok: true,
          msg: r.data.source === "interno"
            ? "Ya registrado · datos completados"
            : "Datos del padrón · revisá y confirmá",
        });
      } else {
        // Fix BN (2026-11-03): si no la encuentra, NO borra el nombre y apellido que ya escribió el vigilador.
        // Fix AS (2026-09-25): la causa real, para que el vigilador sepa qué pasó.
        setStatus({ ok: false, msg: r.data?.reason === "error_externo"
          ? "No se pudo consultar el padrón en este momento. Completá los datos a mano."
          : "No figura en la plataforma ni en el padrón. Completá los datos a mano." });
      }
    } catch (e) {
      const isStale = (currentDniRef.current || "").replace(/\D/g, "") !== clean;
      if (!isStale) {
        setStatus({ ok: false, msg: "No se pudo consultar, completá manualmente" });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5, margin: "2px 0 6px" }}>
      <style>{`@keyframes dni-spin{to{transform:rotate(360deg)}}.dni-spin{animation:dni-spin .8s linear infinite}`}</style>
      <button
        type="button"
        onClick={lookup}
        disabled={loading}
        data-testid={`${testIdPrefix}-lookup-btn`}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6, alignSelf: "flex-start",
          padding: "8px 14px", border: "1.5px solid #BF3437",
          background: loading ? "var(--invict-bone-2)" : "#fff", color: "#BF3437",
          fontWeight: 800, fontSize: 11, borderRadius: 6, cursor: loading ? "wait" : "pointer",
          textTransform: "uppercase", letterSpacing: "0.04em",
        }}
      >
        {loading ? <Loader2 size={13} className="dni-spin" /> : <Search size={13} />}
        {loading ? "Buscando..." : "Buscar datos por DNI"}
      </button>
      {status && (
        <span
          data-testid={`${testIdPrefix}-lookup-status`}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700,
            color: status.ok ? "#16a34a" : "var(--invict-text-muted)",
          }}
        >
          {status.ok ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />} {status.msg}
        </span>
      )}
    </div>
  );
}
